/* ==========================================================================
   eg-lymphoria-brown — landing page behaviour

   Gallery, accordions and carousels are presentation only. The part that
   matters is the offer block: the chosen tier and the refill toggle drive every
   price on the page and the add-to-cart payload.
   ========================================================================== */
(function () {
  'use strict';

  var SHIP_OFFSET_DAYS = 4;

  // The reviews rail alternates photo and quote cards, so on a narrow screen it
  // pages ten times. Past this many the dots stop being one-per-page and become
  // a position indicator instead, which is what the design shows.
  var MAX_DOTS = 5;

  var MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  /* ---------------------------------------------------------------- utils */

  function on(el, evt, fn) {
    if (el) el.addEventListener(evt, fn);
  }

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  /* ------------------------------------------------------------------ boot */

  function init(root) {
    if (!root || root.dataset.eglbInit === 'true') return;
    root.dataset.eglbInit = 'true';

    var subscribeBox = root.querySelector('[data-eglb-subscribe]');
    var state = {
      tier: root.querySelector('[data-eglb-tier].is-selected') || root.querySelector('[data-eglb-tier]'),
      // the design ships with refills opted in
      subscribe: !!(subscribeBox && subscribeBox.checked)
    };

    countdown(root);
    anchors(root);
    gallery(root);
    accordions(root);
    carousels(root);
    shipDate(root);
    offer(root, state);
    stickyBar(root);
  }

  /* ------------------------------------------------------------ countdown */

  function countdown(root) {
    var el = root.querySelector('[data-eglb-countdown]');
    if (!el) return;

    var TOTAL = 60 * 60 * 1000; // one hour
    var key = 'eglbSaleEnd';
    var end = parseInt(sessionStorage.getItem(key) || '0', 10);

    if (!end || end < Date.now()) {
      end = Date.now() + TOTAL;
      try {
        sessionStorage.setItem(key, String(end));
      } catch (e) {
        /* private mode — keep the in-memory value */
      }
    }

    function tick() {
      var left = Math.max(0, end - Date.now());
      var s = Math.floor(left / 1000);
      el.textContent = pad(Math.floor(s / 3600)) + ':' + pad(Math.floor((s % 3600) / 60)) + ':' + pad(s % 60);
      if (left <= 0) {
        end = Date.now() + TOTAL;
        try {
          sessionStorage.setItem(key, String(end));
        } catch (e) {
          /* noop */
        }
      }
    }

    tick();
    setInterval(tick, 1000);
  }

  /* -------------------------------------------------------- anchor scroll */

  function anchors(root) {
    each(root.querySelectorAll('[data-eglb-scroll]'), function (link) {
      on(link, 'click', function (event) {
        var target = root.querySelector(link.getAttribute('href'));
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  /* -------------------------------------------------------------- gallery */

  function gallery(root) {
    var track = root.querySelector('[data-eglb-gallery-track]');
    var slides = track ? track.querySelectorAll('[data-eglb-gallery-slide]') : [];
    var thumbs = root.querySelectorAll('[data-eglb-thumb]');
    var strip = root.querySelector('[data-eglb-thumbs]');
    if (!track || !slides.length || !thumbs.length) return;

    each(thumbs, function (thumb, i) {
      on(thumb, 'click', function () {
        if (!slides[i]) return;
        track.scrollTo({ left: slides[i].offsetLeft - slides[0].offsetLeft, behavior: 'smooth' });
      });
    });

    function sync() {
      var width = slides[0].getBoundingClientRect().width;
      var index = width > 0 ? Math.round(track.scrollLeft / width) : 0;
      index = Math.max(0, Math.min(index, thumbs.length - 1));

      each(thumbs, function (thumb, i) {
        thumb.classList.toggle('is-active', i === index);
      });

      // keep the active thumb inside the (scrollable) strip
      if (strip && strip.scrollWidth > strip.clientWidth) {
        var t = thumbs[index];
        var left = t.offsetLeft - strip.offsetLeft;
        if (left < strip.scrollLeft || left + t.offsetWidth > strip.scrollLeft + strip.clientWidth) {
          strip.scrollTo({ left: left - (strip.clientWidth - t.offsetWidth) / 2, behavior: 'smooth' });
        }
      }
    }

    on(track, 'scroll', sync);
    window.addEventListener('resize', sync);
    sync();
  }

  /* ----------------------------------------------------------- accordions */

  function accordions(root) {
    var items = root.querySelectorAll('[data-eglb-acc]');

    function setOpen(item, open) {
      item.classList.toggle('is-open', open);
      var toggle = item.querySelector('[data-eglb-acc-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', String(open));
    }

    each(items, function (item) {
      var toggle = item.querySelector('[data-eglb-acc-toggle]');
      if (!toggle) return;

      on(toggle, 'click', function () {
        var willOpen = !item.classList.contains('is-open');
        var group = item.closest('[data-eglb-acc-group]');

        // grouped accordions behave as one: opening a card closes its siblings
        if (group && willOpen) {
          each(group.querySelectorAll('[data-eglb-acc]'), function (sibling) {
            if (sibling !== item) setOpen(sibling, false);
          });
        }

        setOpen(item, willOpen);
      });
    });

    // on mobile the whole card is the hit area, not just the +/- button
    each(root.querySelectorAll('[data-eglb-acc-surface]'), function (surface) {
      on(surface, 'click', function (event) {
        var toggle = surface.querySelector('[data-eglb-acc-toggle]');
        if (!toggle || toggle.offsetParent === null) return; // button hidden = desktop
        if (toggle.contains(event.target)) return; // its own handler will fire
        toggle.click();
      });
    });
  }

  /* ------------------------------------------------------------ carousels */

  /*
   * Scroll-snap carousel: the dot count is the number of scroll *positions*,
   * not slides — cards already on screen don't get their own dot.
   */
  function carousels(root) {
    each(root.querySelectorAll('[data-eglb-carousel]'), function (carousel) {
      var track = carousel.querySelector('[data-eglb-track]');
      if (!track) return;
      var dotsHost = carousel.querySelector('[data-eglb-dots]');
      var prev = carousel.querySelector('[data-eglb-prev]');
      var next = carousel.querySelector('[data-eglb-next]');

      /*
       * A slide can split at a breakpoint: the reviews rail pages a whole
       * review on desktop, but on mobile the pair unwraps (its wrapper goes
       * display:contents) and each panel scrolls on its own. Whichever set is
       * actually laid out is the one to page over, so this is read fresh rather
       * than captured once.
       */
      function slides() {
        var panels = track.querySelectorAll('[data-eglb-subslide]');
        if (panels.length && getComputedStyle(panels[0].parentNode).display === 'contents') {
          return panels;
        }
        return track.querySelectorAll('[data-eglb-slide]');
      }

      if (!slides().length) return;

      var current = 1;
      var pages = 1;
      var dotCount = 0;

      // dot n <-> page, spread evenly when there are more pages than dots
      function pageForDot(i) {
        if (dotCount < 2) return 1;
        return Math.round((i * (pages - 1)) / (dotCount - 1)) + 1;
      }

      function dotForPage(page) {
        if (pages < 2 || dotCount < 2) return 0;
        return Math.round(((page - 1) * (dotCount - 1)) / (pages - 1));
      }

      function step() {
        var width = slides()[0].getBoundingClientRect().width;
        var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return width + gap;
      }

      function scrollToPage(page) {
        var items = slides();
        var index = Math.max(0, Math.min(page, pages) - 1);
        track.scrollTo({ left: items[index].offsetLeft - items[0].offsetLeft, behavior: 'smooth' });
      }

      function buildDots(count) {
        dotsHost.innerHTML = '';
        dotCount = count;
        for (var i = 0; i < count; i++) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'eglb__dot';
          dot.dataset.index = String(i);
          dot.setAttribute('aria-label', 'Go to slide ' + pageForDot(i));
          dotsHost.appendChild(dot);
        }
      }

      function sync() {
        var size = step();
        if (size <= 0) return;

        var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        var perView = Math.max(1, Math.floor((track.clientWidth + gap) / size));
        pages = Math.max(1, slides().length - (perView - 1));
        current = Math.max(1, Math.min(pages, Math.round(track.scrollLeft / size) + 1));

        if (dotsHost) {
          var wanted = Math.min(pages, MAX_DOTS);
          if (dotCount !== wanted) buildDots(wanted);
          var active = dotForPage(current);
          each(dotsHost.children, function (dot, i) {
            dot.classList.toggle('is-active', i === active);
          });
          dotsHost.hidden = pages < 2;
        }
        if (prev) prev.disabled = current <= 1;
        if (next) next.disabled = current >= pages;
      }

      if (dotsHost) {
        on(dotsHost, 'click', function (event) {
          var dot = event.target.closest('[data-index]');
          if (dot) scrollToPage(pageForDot(Number(dot.dataset.index)));
        });
      }

      on(prev, 'click', function () {
        scrollToPage(current - 1);
      });
      on(next, 'click', function () {
        scrollToPage(current + 1);
      });

      on(track, 'scroll', sync);
      window.addEventListener('resize', sync);
      sync();
    });
  }

  /* ------------------------------------------------------------ ship date */

  function shipDate(root) {
    var el = root.querySelector('[data-eglb-ship-date]');
    if (!el) return;

    var d = new Date();
    d.setDate(d.getDate() + SHIP_OFFSET_DAYS);

    var day = d.getDate();
    var suffix = 'th';
    if (day % 10 === 1 && day !== 11) suffix = 'st';
    else if (day % 10 === 2 && day !== 12) suffix = 'nd';
    else if (day % 10 === 3 && day !== 13) suffix = 'rd';

    el.textContent = MONTHS[d.getMonth()] + ' ' + day + suffix;
  }

  /* ---------------------------------------------------- offer / tiers / ATC */

  function offer(root, state) {
    var tiers = root.querySelectorAll('[data-eglb-tier]');
    var subscribeBox = root.querySelector('[data-eglb-subscribe]');
    var frequency = root.querySelector('[data-eglb-frequency]');
    var atcPrice = root.querySelector('[data-eglb-atc-price]');
    var atcCompare = root.querySelector('[data-eglb-atc-compare]');

    /*
     * One toggle drives the whole block: with refills on, every tier shows its
     * selling-plan price and the matching saving; with it off, the list price.
     * The struck price is the compare-at either way, so it never moves.
     */
    function render() {
      var tier = state.tier;
      if (!tier) return;

      var sub = state.subscribe;

      each(tiers, function (t) {
        var selected = t === tier;
        t.classList.toggle('is-selected', selected);
        t.setAttribute('aria-checked', String(selected));

        var price = t.querySelector('[data-eglb-tier-price]');
        var compare = t.querySelector('[data-eglb-tier-compare]');
        var save = t.querySelector('[data-eglb-tier-save]');
        var saveRow = t.querySelector('[data-eglb-tier-save-row]');
        if (price) price.textContent = sub ? t.dataset.priceSub : t.dataset.priceOnce;
        if (compare) compare.textContent = sub ? t.dataset.compareSub : t.dataset.compareOnce;
        if (save) save.textContent = sub ? t.dataset.saveSub : t.dataset.saveOnce;

        var unit = t.querySelector('[data-eglb-tier-unit]');
        if (unit) unit.textContent = sub ? t.dataset.perbottleSub : t.dataset.perbottleOnce;

        /*
         * A saving of zero means the product carries no compare-at for this
         * state, so the struck price would just repeat the live one and is
         * dropped. The "You save" line stays either way — a $0.00 saving is
         * still the answer to what this pack saves.
         */
        var cents = Number(sub ? t.dataset.saveSubCents : t.dataset.saveOnceCents);
        if (compare) compare.hidden = !(cents > 0);
        if (saveRow) saveRow.hidden = false;
      });

      if (atcPrice) atcPrice.textContent = sub ? tier.dataset.priceSub : tier.dataset.priceOnce;
      if (atcCompare) {
        var atcSaved = Number(sub ? tier.dataset.saveSubCents : tier.dataset.saveOnceCents) > 0;
        atcCompare.textContent = atcSaved
          ? (sub ? tier.dataset.compareSub : tier.dataset.compareOnce) || ''
          : '';
      }

      // each pack size refills on its own cadence (4 / 8 / 12 weeks)
      if (frequency && tier.dataset.frequency) frequency.textContent = tier.dataset.frequency;
    }

    each(tiers, function (tier) {
      on(tier, 'click', function () {
        state.tier = tier;
        render();
      });
    });

    on(subscribeBox, 'change', function () {
      state.subscribe = subscribeBox.checked;
      render();
    });

    render();
    addToCart(root, state);
  }

  function addToCart(root, state) {
    var buttons = root.querySelectorAll('[data-eglb-atc]');
    var errors = root.querySelectorAll('[data-eglb-cart-error]');

    var addUrl = root.dataset.cartAddUrl || '/cart/add.js';
    var clearUrl = root.dataset.cartClearUrl || '/cart/clear.js';
    var cartUrl = root.dataset.cartUrl || '/cart.js';
    var checkoutUrl = root.dataset.checkoutUrl || '/checkout';
    var working = false;

    function showError(message) {
      each(errors, function (el) {
        el.textContent = message;
        el.hidden = !message;
      });
    }

    /*
     * Shopify does not always answer with JSON: a bot check or an edge error
     * comes back as an HTML page, sometimes even with a 200. Parsing the body
     * ourselves keeps those from passing for a success.
     */
    function readJSON(res) {
      return res.text().then(function (text) {
        var data = null;
        try {
          data = JSON.parse(text);
        } catch (e) {
          /* not JSON — handled below */
        }
        if (!res.ok || !data) {
          var err = new Error((data && (data.description || data.message)) || '');
          err.status = res.status;
          throw err;
        }
        return data;
      });
    }

    function postJSON(url, body) {
      return fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body || {})
      }).then(readJSON);
    }

    // best effort: a blocked /cart.js must not stop a cart that did fill
    function cartCount() {
      return fetch(cartUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(readJSON)
        .then(function (cart) {
          return cart.item_count;
        })
        .catch(function () {
          return null;
        });
    }

    /*
     * A single line: the chosen pack. Refills and one-time purchase are priced
     * off two different products, so the toggle picks the variant as well as
     * the selling plan — buying one-time must not put the subscription variant
     * in the cart at its subscription price.
     */
    function itemsFor(tier, subscribe) {
      if (!subscribe) {
        return [{ id: Number(tier.dataset.otpVariantId || tier.dataset.variantId), quantity: 1 }];
      }
      var item = { id: Number(tier.dataset.variantId), quantity: 1 };
      if (tier.dataset.sellingPlan) item.selling_plan = Number(tier.dataset.sellingPlan);
      return [item];
    }

    /*
     * The cart only ever holds this one offer, so it is cleared first — but a
     * failed clear must not block the purchase, it only risks a stale line.
     *
     * Checkout is reached only once the cart is known to hold something:
     * Shopify sends an empty cart from /checkout straight to the home page,
     * which is what a silently failed add looked like to the shopper.
     */
    function buy(items, busy) {
      showError('');
      working = true;
      each(busy, function (b) { b.setAttribute('aria-busy', 'true'); });

      return postJSON(clearUrl)
        .catch(function (err) {
          console.warn('[eg-lymphoria-combined] clear cart:', err);
        })
        .then(function () {
          return postJSON(addUrl, { items: items });
        })
        .then(cartCount)
        .then(function (count) {
          if (count === 0) throw new Error('');
          window.location.href = checkoutUrl;
        })
        .catch(function (err) {
          // stay on the page so the shopper can retry rather than bouncing them
          working = false;
          each(busy, function (b) { b.removeAttribute('aria-busy'); });
          showError(
            err.message ||
              'We could not add this to your cart. Please try again, or refresh the page.'
          );
          console.error('[eg-lymphoria-combined] add to cart:', err);
        });
    }

    each(buttons, function (button) {
      on(button, 'click', function () {
        if (working || !state.tier) return;
        var items = itemsFor(state.tier, state.subscribe);
        if (!items[0].id) {
          showError('This pack is unavailable right now. Please pick another one.');
          return;
        }
        buy(items, buttons);
      });
    });
  }

  /* ------------------------------------------------------------ sticky bar */

  function stickyBar(root) {
    var sticky = root.querySelector('[data-eglb-sticky]');
    var anchor = root.querySelector('.eglb__cta');
    if (!sticky || !anchor) return;

    sticky.removeAttribute('hidden');

    function update() {
      // shown as soon as the main add-to-cart has scrolled out of view
      sticky.classList.toggle('is-visible', anchor.getBoundingClientRect().bottom < 0);
    }

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ----------------------------------------------------------------- boot */

  function boot() {
    each(document.querySelectorAll('[data-eglb]'), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor re-renders
  document.addEventListener('shopify:section:load', boot);
})();
